import React, { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  ActionsContainer,
  Button,
  ButtonsContainer,
  ConfirmDismissButton,
  DismissalButtonPanel,
  DismissalCommentPanel,
  DismissalContainer,
  ReviewerCommentInput,
} from './Actions.style';
import {
  useDismissReviewFrameMutation,
  useUpsertDicomJobMutation,
  useUpsertKeypointCollectionLabelsMutation,
  useUpsertPhaseLabelsMutation,
  useUpsertQualityLabelsMutation,
  useUpsertSegmentationLabelsMutation,
  useUpsertViewLabelMutation,
  useUpdateReviewFrameStatusMutation,
} from '../../../../../redux/slices/icardio';
import { IDicomLabelingJob, IReviewFrame, ISegmentationLabel } from 'models/icardio';

interface IProps {
  dicom: any;
  frames: any;
  phaseLabels: any;
  keypointCollectionLabels: any;
  dirtySegmentations: ISegmentationLabel[];
  viewLabel: any;
  qualityLabel: any;
  clearDirtySegmentations: () => void;
  setDataIsSaving: any;
  setErrorOccured: any;
  setViewLabelChangesAreSaved: any;
  setQualityLabelChangesAreSaved: any;
  setPhaseLabelLabelChangesAreSaved: any;
  setKeypointCollectionLabelLabelsChangesAreSaved: any;
  setSegmentationChangesAreSaved: any;
  setDicomJobChangesAreSaved: any;
  dicomLabelingJob: any;
  editMode: boolean;
  activeDicomLabelingJob?: IDicomLabelingJob;
  activeReviewFrame?: IReviewFrame;
  /** When true (reviewer link), save forces new label rows and sets source traceability. */
  isReviewMode: boolean;
}

const Actions: FC<IProps> = props => {
  const {
    dicom,
    frames,
    phaseLabels,
    keypointCollectionLabels,
    dirtySegmentations,
    viewLabel,
    qualityLabel,
    clearDirtySegmentations,
    setDataIsSaving,
    setErrorOccured,
    setViewLabelChangesAreSaved,
    setQualityLabelChangesAreSaved,
    setPhaseLabelLabelChangesAreSaved,
    setKeypointCollectionLabelLabelsChangesAreSaved,
    setSegmentationChangesAreSaved,
    setDicomJobChangesAreSaved,
    editMode,
    activeDicomLabelingJob,
    activeReviewFrame,
    isReviewMode,
  } = props;

  const [upsertDicomJob, { isLoading: upsertDicomJobIsLoading }] = useUpsertDicomJobMutation();
  const [upsertViewLabel, { isLoading: upsertViewLabelIsLoading }] = useUpsertViewLabelMutation();

  const [upsertQualityLabel, { isLoading: upsertQualityLabelIsLoading }] =
    useUpsertQualityLabelsMutation();

  const [upsertPhaseLabels, { isLoading: upsertPhaseLabelsIsLoading }] =
    useUpsertPhaseLabelsMutation();

  const [upsertKeypointCollections, { isLoading: upsertKeypointCollectionsIsLoading }] =
    useUpsertKeypointCollectionLabelsMutation();

  const [upsertSegmentations, { isLoading: upsertSegmentationsIsLoading }] =
    useUpsertSegmentationLabelsMutation();
  const [dismissReviewFrame, { isLoading: dismissReviewFrameIsLoading }] =
    useDismissReviewFrameMutation();
  const [updateReviewFrameStatus, { isLoading: updateReviewFrameStatusIsLoading }] =
    useUpdateReviewFrameStatusMutation();
  const [dismissalIsOpen, setDismissalIsOpen] = useState(false);
  const [reviewerComment, setReviewerComment] = useState('');

  useEffect(() => {
    setDismissalIsOpen(false);
    setReviewerComment(activeReviewFrame?.reviewerComment ?? '');
  }, [activeReviewFrame?.reviewerComment, activeReviewFrame?.uuid, isReviewMode]);

  const viewLabelPayload = useMemo(() => {
    if (!viewLabel?.viewClass) return null;
    return {
      viewClass: viewLabel?.viewClass,
      dicom: dicom?.uuid,
      uuid: viewLabel?.uuid,
    };
  }, [viewLabel]);

  const qualityLabelPayload = useMemo(() => {
    if (!qualityLabel?.qualityClass) return null;
    return qualityLabel;
  }, [qualityLabel]);

  const phaseLabelsPayload = useMemo(
    () =>
      phaseLabels?.filter(
        phaseLabel => phaseLabel?.dicom === dicom?.uuid && !!phaseLabel?.phaseClass
      ),
    [dicom?.uuid, phaseLabels]
  );

  // x, y for some reason are no longer ints, hence converted here
  const keypointCollectionLabelsPayload = useMemo(() => {
    const frameUuids = frames?.map(frame => frame?.uuid);
    return keypointCollectionLabels
      ?.filter(keypointCollectionLabel => frameUuids?.includes(keypointCollectionLabel?.frame))
      ?.map(keypointCollectionLabel => {
        return {
          ...keypointCollectionLabel,
          keypointLabels: keypointCollectionLabel?.keypointLabels?.map(keypointLabel => ({
            ...keypointLabel,
            x: Math.round(keypointLabel?.x),
            y: Math.round(keypointLabel?.y),
          })),
        };
      });
  }, [frames, keypointCollectionLabels]);

  const segmentationsPayload = useMemo(() => {
    const frameUuids = frames?.map(frame => frame?.uuid);
    const filtered = dirtySegmentations?.filter(segmentation =>
      frameUuids?.includes(segmentation?.frame)
    );
    if (!isReviewMode) {
      return filtered;
    }
    // Reviewer workflow only: strip uuid/icid so upsert creates a new row; chain via sourceUuid.
    return filtered?.map(segmentation => ({
      ...segmentation,
      sourceUuid: segmentation.uuid,
      sourceType: 'SEGMENTATION_LABEL',
      uuid: undefined,
      icid: undefined,
    }));
  }, [dirtySegmentations, frames, isReviewMode]);

  const dataIsSaving = useMemo(
    () =>
      upsertViewLabelIsLoading ||
      upsertQualityLabelIsLoading ||
      upsertPhaseLabelsIsLoading ||
      upsertKeypointCollectionsIsLoading ||
      upsertSegmentationsIsLoading ||
      upsertDicomJobIsLoading ||
      dismissReviewFrameIsLoading ||
      updateReviewFrameStatusIsLoading,
    [
      upsertKeypointCollectionsIsLoading,
      upsertQualityLabelIsLoading,
      upsertViewLabelIsLoading,
      upsertPhaseLabelsIsLoading,
      upsertSegmentationsIsLoading,
      upsertDicomJobIsLoading,
      dismissReviewFrameIsLoading,
      updateReviewFrameStatusIsLoading,
    ]
  );

  const dismissButtonIsDisabled = dataIsSaving || !isReviewMode || !activeReviewFrame;
  const saveButtonIsDisabled = dataIsSaving || editMode;

  const handleSaveOnClick = () => {
    if (saveButtonIsDisabled) return; // don't save if disabled

    setDataIsSaving(true);

    if (viewLabelPayload)
      upsertViewLabel(viewLabelPayload)
        ?.then(r => {
          console.log(r);
          setViewLabelChangesAreSaved(true);
        })
        ?.catch(e => {
          console.log(e);
          setErrorOccured(true);
        });

    if (qualityLabelPayload)
      upsertQualityLabel(qualityLabel)
        ?.then(r => {
          console.log(r);
          setQualityLabelChangesAreSaved(true);
        })
        ?.catch(e => {
          console.log(e);
          setErrorOccured(true);
        });

    upsertPhaseLabels(phaseLabelsPayload)
      ?.then(r => {
        console.log(r);
        setPhaseLabelLabelChangesAreSaved(true);
      })
      ?.catch(e => {
        console.log(e);
        setErrorOccured(true);
      });

    upsertKeypointCollections(keypointCollectionLabelsPayload)
      ?.then(r => {
        console.log(r);
        setKeypointCollectionLabelLabelsChangesAreSaved(true);
      })
      ?.catch(e => {
        console.log(e);
        setErrorOccured(true);
      });

    if (segmentationsPayload?.length) {
      const reviewedFrameUuid = frames?.find(
        frame => frame.index === activeReviewFrame?.frameIndex
      )?.uuid;
      const updatesActiveReviewFrame =
        isReviewMode &&
        !!activeReviewFrame &&
        !!reviewedFrameUuid &&
        segmentationsPayload.some(segmentation => segmentation.frame === reviewedFrameUuid);

      upsertSegmentations(segmentationsPayload)
        .unwrap()
        .then(async r => {
          console.log(r);
          clearDirtySegmentations();
          setSegmentationChangesAreSaved(true);

          if (updatesActiveReviewFrame && activeReviewFrame) {
            await updateReviewFrameStatus({
              dicomId: activeReviewFrame.dicomId,
              frameIndex: activeReviewFrame.frameIndex,
              status: 'FIXED',
            }).unwrap();
          }
        })
        .catch(e => {
          console.log(e);
          setErrorOccured(true);
        });
    }

    if (activeDicomLabelingJob)
      upsertDicomJob({ dicomLabelingJob: activeDicomLabelingJob?.uuid }) // send an empty payload, the backend calcualtes completeness
        ?.then(r => {
          console.log(r);
          setDicomJobChangesAreSaved(true);
        })
        ?.catch(e => {
          console.log(e);
          setErrorOccured(true);
        });
  };

  useEffect(() => setDataIsSaving(dataIsSaving), [dataIsSaving, setDataIsSaving]);

  const handleDismissFrameOnClick = () => {
    if (dismissButtonIsDisabled || !activeReviewFrame) return;

    setDismissalIsOpen(true);
  };

  const handleConfirmDismissal = async () => {
    if (dismissButtonIsDisabled || !activeReviewFrame) return;

    try {
      await dismissReviewFrame({
        dicomId: activeReviewFrame.dicomId,
        frameIndex: activeReviewFrame.frameIndex,
        reviewerComment,
      }).unwrap();
      setDismissalIsOpen(false);
    } catch (error) {
      console.error(error);
      setErrorOccured(true);
    }
  };

  const handleCancelDismissal = () => {
    setReviewerComment(activeReviewFrame?.reviewerComment ?? '');
    setDismissalIsOpen(false);
  };

  useHotkeys('p', event => {
    event?.preventDefault();
    handleSaveOnClick();
  });

  useHotkeys('D', event => {
    event?.preventDefault();
    handleDismissFrameOnClick();
  });

  return (
    <ActionsContainer>
      <ButtonsContainer justify="space-between">
        {!dismissalIsOpen && (
          <Button
            $fullWidth={!isReviewMode}
            disabled={saveButtonIsDisabled}
            onClick={handleSaveOnClick}
          >
            (P) Save
          </Button>
        )}
        {/* Edit and Adopt are intentionally unavailable in the frame-review workflow. */}
        {isReviewMode && (
          <DismissalContainer $isOpen={dismissalIsOpen}>
            <DismissalButtonPanel $isOpen={dismissalIsOpen}>
              <Button
                $fullWidth
                disabled={dismissButtonIsDisabled}
                onClick={handleDismissFrameOnClick}
              >
                (D) Dismiss
              </Button>
            </DismissalButtonPanel>
            <DismissalCommentPanel $isOpen={dismissalIsOpen}>
              <ReviewerCommentInput
                aria-label="Review comment"
                onChange={event => setReviewerComment(event.target.value)}
                placeholder="Review comment"
                value={reviewerComment}
              />
              <ConfirmDismissButton
                aria-label="Complete dismissal"
                disabled={dismissButtonIsDisabled}
                onClick={handleConfirmDismissal}
                type="button"
              >
                ✓
              </ConfirmDismissButton>
              <ConfirmDismissButton
                aria-label="Cancel dismissal"
                disabled={dismissButtonIsDisabled}
                onClick={handleCancelDismissal}
                type="button"
              >
                ×
              </ConfirmDismissButton>
            </DismissalCommentPanel>
          </DismissalContainer>
        )}
      </ButtonsContainer>
    </ActionsContainer>
  );
};

export default Actions;
